import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  ShieldCheck, Users, DollarSign, Package, AlertTriangle, 
  CheckCircle, XCircle, FileText, Check, ArrowRight 
} from 'lucide-react';

export const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState<'metrics' | 'users' | 'disputes' | 'payouts' | 'audit'>('metrics');

  const [metrics, setMetrics] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [disputes, setDisputes] = useState<any[]>([]);
  const [payouts, setPayouts] = useState<any[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Status message
  const [actionNotice, setActionNotice] = useState('');

  const fetchAdminData = async () => {
    try {
      const [mRes, uRes, dRes, pRes, aRes] = await Promise.all([
        fetch('/api/admin/metrics'),
        fetch('/api/admin/users'),
        fetch('/api/disputes'),
        fetch('/api/admin/payouts'),
        fetch('/api/admin/audit-logs')
      ]);

      if (mRes.ok) {
        const mData = await mRes.json();
        if (mData.success) setMetrics(mData.metrics);
      }
      if (uRes.ok) {
        const uData = await uRes.json();
        if (uData.success) setUsers(uData.users || []);
      }
      if (dRes.ok) {
        const dData = await dRes.json();
        if (dData.success) setDisputes(dData.disputes || []);
      }
      if (pRes.ok) {
        const pData = await pRes.json();
        if (pData.success) setPayouts(pData.payouts || []);
      }
      if (aRes.ok) {
        const aData = await aRes.json();
        if (aData.success) setAuditLogs(aData.auditLogs || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
  }, []);

  const handleUpdateRole = async (userId: string, newRole: string) => {
    try {
      const res = await fetch(`/api/admin/users/${userId}/role`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole })
      });
      if (res.ok) {
        setActionNotice(`User role updated to ${newRole}`);
        await fetchAdminData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleResolveDispute = async (disputeId: string, resolution: 'resolved_refund' | 'resolved_seller') => {
    try {
      const res = await fetch(`/api/disputes/${disputeId}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resolution,
          notes: resolution === 'resolved_refund' ? 'Full refund approved by staff adjudication.' : 'Seller evidence confirmed valid; funds released.'
        })
      });
      if (res.ok) {
        setActionNotice(`Dispute closed with decision: ${resolution}`);
        await fetchAdminData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdatePayoutStatus = async (payoutId: string, newStatus: string) => {
    try {
      const res = await fetch(`/api/admin/payouts/${payoutId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: newStatus,
          payment_reference: `TXN_${Date.now()}`
        })
      });
      if (res.ok) {
        setActionNotice(`Payout status updated to ${newStatus}`);
        await fetchAdminData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      
      {/* Header */}
      <div className="p-8 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder shadow-lg flex items-center justify-between">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-rose-500/10 text-rose-500 border border-rose-500/20 mb-2">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Master Administration Console</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-brand-lightText dark:text-brand-darkText">
            Platform Management & Operations
          </h1>
          <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">
            Real-time metrics from Cloudflare D1 SQLite database, user management, and dispute resolution
          </p>
        </div>
      </div>

      {actionNotice && (
        <div className="p-4 rounded-2xl bg-brand-blue/10 border border-brand-blue/30 text-brand-blue dark:text-brand-accent text-xs font-bold flex items-center justify-between">
          <span>{actionNotice}</span>
          <button onClick={() => setActionNotice('')}>✕</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-brand-lightBorder dark:border-brand-darkBorder pb-4">
        {[
          { id: 'metrics', label: 'Overview Metrics', icon: DollarSign },
          { id: 'users', label: `Users & Roles (${users.length})`, icon: Users },
          { id: 'disputes', label: `Disputes & Claims (${disputes.length})`, icon: AlertTriangle },
          { id: 'payouts', label: `Payout Approvals (${payouts.length})`, icon: DollarSign },
          { id: 'audit', label: `Audit Trail (${auditLogs.length})`, icon: FileText },
        ].map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id as any)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-bold transition-all ${
                isActive
                  ? 'bg-brand-blue text-white shadow-glow-blue'
                  : 'bg-brand-lightSec dark:bg-brand-darkCard text-brand-lightSecText hover:text-brand-lightText'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab 1: Database Metrics */}
      {tab === 'metrics' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-1">
            <span className="text-xs font-bold uppercase text-brand-lightSecText">Total GMV Volume</span>
            <p className="text-3xl font-black text-brand-blue dark:text-brand-accent">
              ${((metrics?.totalVolumeCents || 0) / 100).toFixed(2)}
            </p>
            <span className="text-[11px] text-emerald-500 font-semibold">100% database-backed</span>
          </div>

          <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-1">
            <span className="text-xs font-bold uppercase text-brand-lightSecText">Platform Net Fees (8%)</span>
            <p className="text-3xl font-black text-emerald-600 dark:text-emerald-400">
              ${((metrics?.totalFeesEarnedCents || 0) / 100).toFixed(2)}
            </p>
            <span className="text-[11px] text-brand-lightSecText">Collected commission</span>
          </div>

          <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-1">
            <span className="text-xs font-bold uppercase text-brand-lightSecText">Registered Users</span>
            <p className="text-3xl font-black text-brand-lightText dark:text-brand-darkText">
              {metrics?.totalUsers || 0}
            </p>
            <span className="text-[11px] text-brand-lightSecText">Active across all roles</span>
          </div>

          <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-1">
            <span className="text-xs font-bold uppercase text-brand-lightSecText">Active Open Disputes</span>
            <p className="text-3xl font-black text-rose-500">
              {metrics?.activeDisputes || 0}
            </p>
            <span className="text-[11px] text-rose-500 font-semibold">Requiring staff review</span>
          </div>
        </div>
      )}

      {/* Tab 2: Users & Roles Management */}
      {tab === 'users' && (
        <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-4">
          <h2 className="font-bold text-lg">Platform Users & RBAC Permissions</h2>
          <div className="divide-y divide-brand-lightBorder dark:divide-brand-darkBorder text-xs">
            {users.map((u) => (
              <div key={u.id} className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-brand-lightText dark:text-brand-darkText">{u.username}</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-brand-blue/15 text-brand-blue">
                      {u.role}
                    </span>
                  </div>
                  <p className="text-brand-lightSecText mt-0.5">{u.email} • ID: {u.id}</p>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-brand-lightSecText font-medium">Role:</span>
                  <select
                    value={u.role}
                    onChange={(e) => handleUpdateRole(u.id, e.target.value)}
                    className="p-1.5 rounded-lg border bg-brand-lightSec dark:bg-brand-darkElevated font-semibold"
                  >
                    <option value="buyer">buyer</option>
                    <option value="seller">seller</option>
                    <option value="booster">booster</option>
                    <option value="support">support</option>
                    <option value="admin">admin</option>
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab 3: Disputes Console */}
      {tab === 'disputes' && (
        <div className="space-y-6">
          <h2 className="font-bold text-lg">72-Hour Buyer Protection Claims</h2>

          {disputes.length > 0 ? (
            <div className="space-y-4">
              {disputes.map((d) => (
                <div key={d.id} className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-mono text-xs font-bold text-brand-lightSecText">Order #{d.order_number}</span>
                      <h3 className="font-bold text-sm text-rose-500 mt-0.5">Reason: {d.reason}</h3>
                    </div>
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase bg-rose-500/10 text-rose-500">
                      {d.status}
                    </span>
                  </div>

                  <p className="text-xs text-brand-lightText dark:text-brand-darkText p-3 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated">
                    Buyer Evidence: "{d.evidence_text}"
                  </p>

                  {d.status === 'open' || d.status === 'under_review' ? (
                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={() => handleResolveDispute(d.id, 'resolved_refund')}
                        className="px-4 py-2 rounded-xl font-bold text-xs text-white bg-rose-600 hover:bg-rose-700 transition-colors"
                      >
                        Approve Full Refund to Buyer
                      </button>
                      <button
                        onClick={() => handleResolveDispute(d.id, 'resolved_seller')}
                        className="px-4 py-2 rounded-xl font-bold text-xs text-white bg-emerald-600 hover:bg-emerald-700 transition-colors"
                      >
                        Rule in Favor of Seller
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-emerald-500 font-bold">
                      Resolved: {d.resolution_notes || d.status}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center text-xs text-brand-lightSecText bg-white dark:bg-brand-darkCard rounded-3xl border">
              Zero pending disputes. All marketplace orders completed normally!
            </div>
          )}
        </div>
      )}

      {/* Tab 4: Payout Approvals */}
      {tab === 'payouts' && (
        <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-4">
          <h2 className="font-bold text-lg">Seller Withdrawal Disbursements</h2>
          <div className="divide-y divide-brand-lightBorder dark:divide-brand-darkBorder text-xs">
            {payouts.map((p) => (
              <div key={p.id} className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <span className="font-bold text-sm block">${(p.amount_cents / 100).toFixed(2)} USD • {p.method}</span>
                  <span className="text-brand-lightSecText">Seller: {p.store_name} ({p.seller_email})</span>
                  <span className="block text-[11px] text-brand-lightSecText mt-0.5">Dest: {p.destination_info}</span>
                </div>

                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded font-bold uppercase text-[10px] bg-amber-500/10 text-amber-500">
                    {p.status}
                  </span>
                  {p.status === 'requested' && (
                    <>
                      <button
                        onClick={() => handleUpdatePayoutStatus(p.id, 'paid')}
                        className="px-3 py-1.5 rounded-lg font-bold text-xs text-white bg-emerald-600 hover:bg-emerald-700"
                      >
                        Approve & Pay
                      </button>
                      <button
                        onClick={() => handleUpdatePayoutStatus(p.id, 'rejected')}
                        className="px-3 py-1.5 rounded-lg font-bold text-xs text-white bg-rose-600 hover:bg-rose-700"
                      >
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab 5: System Audit Logs */}
      {tab === 'audit' && (
        <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-4">
          <h2 className="font-bold text-lg">System Audit Log Trail</h2>
          <div className="divide-y divide-brand-lightBorder dark:divide-brand-darkBorder text-xs font-mono">
            {auditLogs.map((log) => (
              <div key={log.id} className="py-2.5 flex items-center justify-between">
                <div>
                  <strong className="text-brand-blue">{log.action}</strong> by {log.actor_username || 'System'} ({log.resource_type})
                  <span className="block text-[10px] text-brand-lightSecText font-sans">{log.details_json}</span>
                </div>
                <span className="text-[10px] text-brand-lightSecText">{new Date(log.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
